import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
  increment
} from 'firebase/firestore';
import { db } from './config';

// Time slots collection (admin-created available slots)
const getTimeSlotsCollection = () => {
  return collection(db, 'timeSlots');
};

// Bookings collection
const getBookingsCollection = () => {
  return collection(db, 'bookings');
};

// Requests collection
const getRequestsCollection = () => {
  return collection(db, 'requests');
};

// Create a time slot (admin only)
// adminUserId - the ID of the admin creating the slot (will be automatically available)
export const createTimeSlot = async (slotData, adminUserId = null) => {
  try {
    const slotsRef = getTimeSlotsCollection();
    const docRef = await addDoc(slotsRef, {
      ...slotData,
      bookingCount: 0,
      createdAt: serverTimestamp()
    });

    // Automatically add the creating admin as available for this slot
    if (adminUserId) {
      await setHostAvailability(adminUserId, docRef.id, true);
    }

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating time slot:', error);
    return { success: false, error };
  }
};

// Create multiple time slots (batch creation)
// adminUserId - the ID of the admin creating the slots (will be automatically available)
export const createMultipleTimeSlots = async (slotsArray, adminUserId = null) => {
  try {
    const slotsRef = getTimeSlotsCollection();
    const promises = slotsArray.map(slotData =>
      addDoc(slotsRef, {
        ...slotData,
        bookingCount: 0,
        createdAt: serverTimestamp()
      })
    );

    const results = await Promise.all(promises);

    // Automatically add the creating admin as available for all new slots
    if (adminUserId) {
      const availabilityPromises = results.map(docRef =>
        setHostAvailability(adminUserId, docRef.id, true)
      );
      await Promise.all(availabilityPromises);
    }

    return { success: true, count: slotsArray.length };
  } catch (error) {
    console.error('Error creating multiple time slots:', error);
    return { success: false, error };
  }
};

// Get all time slots
export const getTimeSlots = async () => {
  try {
    const slotsRef = getTimeSlotsCollection();
    const q = query(slotsRef, orderBy('date'), orderBy('startTime'));
    const querySnapshot = await getDocs(q);
    const slots = [];
    querySnapshot.forEach((doc) => {
      slots.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, slots };
  } catch (error) {
    console.error('Error getting time slots:', error);
    return { success: false, error };
  }
};

// Get time slots by date
export const getTimeSlotsByDate = async (date) => {
  try {
    const slotsRef = getTimeSlotsCollection();
    const q = query(
      slotsRef,
      where('date', '==', date),
      orderBy('startTime')
    );
    const querySnapshot = await getDocs(q);
    const slots = [];
    querySnapshot.forEach((doc) => {
      slots.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, slots };
  } catch (error) {
    console.error('Error getting time slots by date:', error);
    return { success: false, error };
  }
};

// Helper function to assign room based on availability
const assignRoom = (existingBookings) => {
  const roomPriority = ['A', 'B', 'C', 'D'];
  const usedRooms = new Set(
    existingBookings
      .filter(booking => booking.room)
      .map(booking => booking.room)
  );

  // Find first available room following priority
  for (const room of roomPriority) {
    if (!usedRooms.has(room)) {
      return room;
    }
  }

  // If all rooms are taken, return null
  return null;
};

// Create a booking
export const createBooking = async (bookingData) => {
  try {
    const bookingsRef = getBookingsCollection();

    // Check if slot is still available
    const slotRef = doc(db, 'timeSlots', bookingData.slotId);
    const slotSnap = await getDoc(slotRef);

    if (!slotSnap.exists()) {
      return { success: false, error: 'Time slot not found' };
    }

    const slotData = slotSnap.data();

    // Get all existing bookings for this slot (for room assignment)
    const slotBookingsQuery = query(
      bookingsRef,
      where('slotId', '==', bookingData.slotId),
      where('status', '!=', 'cancelled')
    );
    const slotBookingsSnapshot = await getDocs(slotBookingsQuery);
    const existingSlotBookings = [];
    slotBookingsSnapshot.forEach((doc) => {
      existingSlotBookings.push({ id: doc.id, ...doc.data() });
    });

    // If a specific host is selected, check if that host is already booked
    if (bookingData.hostId) {
      const hostAlreadyBooked = existingSlotBookings.some(
        booking => booking.hostId === bookingData.hostId
      );

      if (hostAlreadyBooked) {
        return { success: false, error: 'This session host is already booked for this time slot' };
      }
    } else {
      // No host specified - use old capacity-based check
      if (slotData.bookingCount >= slotData.capacity) {
        return { success: false, error: 'Time slot is full' };
      }
    }

    // Assign room automatically (A, B, C, D priority)
    const assignedRoom = assignRoom(existingSlotBookings);

    if (!assignedRoom) {
      return { success: false, error: 'All rooms are currently booked for this time slot. Please select a different time.' };
    }

    // Create booking with room assignment
    const docRef = await addDoc(bookingsRef, {
      ...bookingData,
      room: assignedRoom,
      status: 'confirmed',
      createdAt: serverTimestamp()
    });

    // Increment booking count
    await updateDoc(slotRef, {
      bookingCount: increment(1)
    });

    return { success: true, id: docRef.id, room: assignedRoom };
  } catch (error) {
    console.error('Error creating booking:', error);
    return { success: false, error };
  }
};

// Get user's bookings
export const getUserBookings = async (email) => {
  try {
    const bookingsRef = getBookingsCollection();
    const q = query(bookingsRef, where('email', '==', email), orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, bookings };
  } catch (error) {
    console.error('Error getting user bookings:', error);
    return { success: false, error };
  }
};

// Create a request for a custom time
export const createRequest = async (requestData) => {
  try {
    const requestsRef = getRequestsCollection();
    const docRef = await addDoc(requestsRef, {
      ...requestData,
      status: 'pending',
      createdAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating request:', error);
    return { success: false, error };
  }
};

// Get all requests (admin only)
export const getRequests = async () => {
  try {
    const requestsRef = getRequestsCollection();
    const q = query(requestsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const requests = [];
    querySnapshot.forEach((doc) => {
      requests.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, requests };
  } catch (error) {
    console.error('Error getting requests:', error);
    return { success: false, error };
  }
};

// Delete time slot
export const deleteTimeSlot = async (slotId) => {
  try {
    const slotRef = doc(db, 'timeSlots', slotId);
    await deleteDoc(slotRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting time slot:', error);
    return { success: false, error };
  }
};

// Delete all time slots (admin only - for clearing old data)
export const deleteAllTimeSlots = async () => {
  try {
    const slotsRef = getTimeSlotsCollection();
    const querySnapshot = await getDocs(slotsRef);
    const deletePromises = [];
    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    await Promise.all(deletePromises);
    return { success: true, count: deletePromises.length };
  } catch (error) {
    console.error('Error deleting all time slots:', error);
    return { success: false, error };
  }
};

// Get all bookings (admin only)
export const getAllBookings = async () => {
  try {
    const bookingsRef = getBookingsCollection();
    const q = query(bookingsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const bookings = [];
    querySnapshot.forEach((doc) => {
      bookings.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, bookings };
  } catch (error) {
    console.error('Error getting all bookings:', error);
    return { success: false, error };
  }
};

// Update request status (admin only)
export const updateRequestStatus = async (requestId, status, notes = '') => {
  try {
    const requestRef = doc(db, 'requests', requestId);
    await updateDoc(requestRef, {
      status,
      notes,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Error updating request status:', error);
    return { success: false, error };
  }
};

// Delete request (admin only)
export const deleteRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'requests', requestId);
    await deleteDoc(requestRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting request:', error);
    return { success: false, error };
  }
};

// Cancel booking (admin only)
export const cancelBooking = async (bookingId, slotId, cancellationNote = '') => {
  try {
    const bookingRef = doc(db, 'bookings', bookingId);
    const slotRef = doc(db, 'timeSlots', slotId);

    // Update booking with cancellation info
    await updateDoc(bookingRef, {
      status: 'cancelled',
      cancellationNote,
      cancelledAt: serverTimestamp()
    });

    // Decrement slot booking count
    await updateDoc(slotRef, {
      bookingCount: increment(-1)
    });

    return { success: true };
  } catch (error) {
    console.error('Error cancelling booking:', error);
    return { success: false, error };
  }
};

// ===== SESSION HOSTS MANAGEMENT =====

// Hosts collection
const getHostsCollection = () => {
  return collection(db, 'sessionHosts');
};

// Host availability collection
const getHostAvailabilityCollection = () => {
  return collection(db, 'hostAvailability');
};

// Create or update a session host
export const saveHost = async (hostData) => {
  try {
    const hostsRef = getHostsCollection();

    if (hostData.id) {
      // Update existing host
      const hostRef = doc(db, 'sessionHosts', hostData.id);
      await updateDoc(hostRef, {
        name: hostData.name,
        email: hostData.email,
        initials: hostData.initials,
        updatedAt: serverTimestamp()
      });
      return { success: true, id: hostData.id };
    } else {
      // Create new host
      const docRef = await addDoc(hostsRef, {
        ...hostData,
        createdAt: serverTimestamp()
      });
      return { success: true, id: docRef.id };
    }
  } catch (error) {
    console.error('Error saving host:', error);
    return { success: false, error };
  }
};

// Get all session hosts
export const getHosts = async () => {
  try {
    const hostsRef = getHostsCollection();
    const querySnapshot = await getDocs(hostsRef);
    const hosts = [];
    querySnapshot.forEach((doc) => {
      hosts.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, hosts };
  } catch (error) {
    console.error('Error getting hosts:', error);
    return { success: false, error };
  }
};

// Delete a session host
export const deleteHost = async (hostId) => {
  try {
    const hostRef = doc(db, 'sessionHosts', hostId);
    await deleteDoc(hostRef);

    // Also delete all availability records for this host
    const availRef = getHostAvailabilityCollection();
    const q = query(availRef, where('hostId', '==', hostId));
    const querySnapshot = await getDocs(q);
    const deletePromises = [];
    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    await Promise.all(deletePromises);

    return { success: true };
  } catch (error) {
    console.error('Error deleting host:', error);
    return { success: false, error };
  }
};

// Set host availability for specific time slots
export const setHostAvailability = async (hostId, slotId, isAvailable) => {
  try {
    const availRef = getHostAvailabilityCollection();

    // Check if availability record exists
    const q = query(
      availRef,
      where('hostId', '==', hostId),
      where('slotId', '==', slotId)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Create new availability record
      if (isAvailable) {
        await addDoc(availRef, {
          hostId,
          slotId,
          isAvailable: true,
          createdAt: serverTimestamp()
        });
      }
    } else {
      // Update or delete existing record
      const availDoc = querySnapshot.docs[0];
      if (isAvailable) {
        await updateDoc(availDoc.ref, {
          isAvailable: true,
          updatedAt: serverTimestamp()
        });
      } else {
        await deleteDoc(availDoc.ref);
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error setting host availability:', error);
    return { success: false, error };
  }
};

// Get available hosts for a specific time slot
export const getAvailableHostsForSlot = async (slotId) => {
  try {
    const availRef = getHostAvailabilityCollection();
    const q = query(availRef, where('slotId', '==', slotId), where('isAvailable', '==', true));
    const querySnapshot = await getDocs(q);

    const hostIds = [];
    querySnapshot.forEach((doc) => {
      hostIds.push(doc.data().hostId);
    });

    // Get host details
    if (hostIds.length === 0) {
      return { success: true, hosts: [] };
    }

    const hostsRef = getHostsCollection();
    const hostsSnapshot = await getDocs(hostsRef);
    const hosts = [];
    hostsSnapshot.forEach((doc) => {
      if (hostIds.includes(doc.id)) {
        hosts.push({ id: doc.id, ...doc.data() });
      }
    });

    return { success: true, hosts };
  } catch (error) {
    console.error('Error getting available hosts:', error);
    return { success: false, error };
  }
};

// Get all host availability records
export const getAllHostAvailability = async () => {
  try {
    const availRef = getHostAvailabilityCollection();
    const querySnapshot = await getDocs(availRef);
    const availability = [];
    querySnapshot.forEach((doc) => {
      availability.push({ id: doc.id, ...doc.data() });
    });
    return { success: true, availability };
  } catch (error) {
    console.error('Error getting host availability:', error);
    return { success: false, error };
  }
};

// Get bookings by host for a specific slot
export const getBookingsByHostForSlot = async (slotId) => {
  try {
    const bookingsRef = getBookingsCollection();
    const q = query(bookingsRef, where('slotId', '==', slotId), where('status', '!=', 'cancelled'));
    const querySnapshot = await getDocs(q);

    const bookingsByHost = {};
    querySnapshot.forEach((doc) => {
      const booking = doc.data();
      const hostId = booking.hostId || 'unassigned';
      if (!bookingsByHost[hostId]) {
        bookingsByHost[hostId] = [];
      }
      bookingsByHost[hostId].push({ id: doc.id, ...booking });
    });

    return { success: true, bookingsByHost };
  } catch (error) {
    console.error('Error getting bookings by host:', error);
    return { success: false, error };
  }
};

// Add current user to all existing time slots (utility function for admins)
export const addCurrentUserToAllSlots = async (userId) => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    // Get all time slots
    const slotsResult = await getTimeSlots();
    if (!slotsResult.success) {
      return { success: false, error: 'Failed to get time slots' };
    }

    // Get existing availability records for this user
    const availRef = getHostAvailabilityCollection();
    const q = query(availRef, where('hostId', '==', userId));
    const querySnapshot = await getDocs(q);

    const existingSlotIds = new Set();
    querySnapshot.forEach((doc) => {
      existingSlotIds.add(doc.data().slotId);
    });

    // Add user to slots where they're not already available
    const promises = [];
    slotsResult.slots.forEach(slot => {
      if (!existingSlotIds.has(slot.id)) {
        promises.push(setHostAvailability(userId, slot.id, true));
      }
    });

    await Promise.all(promises);

    return { success: true, count: promises.length };
  } catch (error) {
    console.error('Error adding user to all slots:', error);
    return { success: false, error };
  }
};

// ===== DIAGNOSTIC AND MIGRATION UTILITIES =====

// Diagnose availability ID mismatches
// Checks if hostAvailability records use Auth UIDs instead of Firestore document IDs
export const diagnoseAvailabilityIdMismatches = async (users) => {
  try {
    // Get all hostAvailability records
    const availRef = getHostAvailabilityCollection();
    const querySnapshot = await getDocs(availRef);

    const availabilityRecords = [];
    querySnapshot.forEach((doc) => {
      availabilityRecords.push({ id: doc.id, ...doc.data() });
    });

    // Build lookup maps for users
    const usersByFirestoreId = {};
    const usersByAuthUid = {};
    users.forEach(user => {
      if (user.id) {
        usersByFirestoreId[user.id] = user;
      }
      if (user.uid) {
        usersByAuthUid[user.uid] = user;
      }
    });

    // Analyze each availability record
    const mismatches = [];
    const orphaned = [];
    const correct = [];

    availabilityRecords.forEach(avail => {
      const hostId = avail.hostId;

      // Check if hostId matches a Firestore document ID
      if (usersByFirestoreId[hostId]) {
        correct.push({
          recordId: avail.id,
          hostId: hostId,
          slotId: avail.slotId,
          userName: usersByFirestoreId[hostId].name,
          status: 'correct'
        });
      }
      // Check if hostId matches an Auth UID (mismatch)
      else if (usersByAuthUid[hostId]) {
        const user = usersByAuthUid[hostId];
        mismatches.push({
          recordId: avail.id,
          currentHostId: hostId,
          correctHostId: user.id,
          slotId: avail.slotId,
          userName: user.name,
          userEmail: user.email,
          status: 'mismatch'
        });
      }
      // Orphaned record (doesn't match any user)
      else {
        orphaned.push({
          recordId: avail.id,
          hostId: hostId,
          slotId: avail.slotId,
          status: 'orphaned'
        });
      }
    });

    return {
      success: true,
      summary: {
        total: availabilityRecords.length,
        correct: correct.length,
        mismatches: mismatches.length,
        orphaned: orphaned.length
      },
      mismatches,
      orphaned,
      correct
    };
  } catch (error) {
    console.error('Error diagnosing availability ID mismatches:', error);
    return { success: false, error: error.message };
  }
};

// Migrate availability records from Auth UIDs to Firestore document IDs
export const migrateAvailabilityIds = async (mismatches) => {
  try {
    const availRef = getHostAvailabilityCollection();
    const migrated = [];
    const failed = [];

    for (const mismatch of mismatches) {
      try {
        // Get the old record
        const oldRecordRef = doc(db, 'hostAvailability', mismatch.recordId);

        // Delete the old record
        await deleteDoc(oldRecordRef);

        // Create new record with correct Firestore ID
        await addDoc(availRef, {
          hostId: mismatch.correctHostId,
          slotId: mismatch.slotId,
          isAvailable: true,
          createdAt: serverTimestamp(),
          migratedAt: serverTimestamp(),
          migratedFrom: mismatch.currentHostId
        });

        migrated.push({
          userName: mismatch.userName,
          slotId: mismatch.slotId,
          oldId: mismatch.currentHostId,
          newId: mismatch.correctHostId
        });
      } catch (error) {
        console.error('Error migrating record:', mismatch.recordId, error);
        failed.push({
          recordId: mismatch.recordId,
          error: error.message
        });
      }
    }

    return {
      success: true,
      migrated: migrated.length,
      failed: failed.length,
      details: { migrated, failed }
    };
  } catch (error) {
    console.error('Error migrating availability IDs:', error);
    return { success: false, error: error.message };
  }
};

// Delete orphaned availability records
export const deleteOrphanedAvailability = async (orphanedRecords) => {
  try {
    const deleted = [];
    const failed = [];

    for (const orphan of orphanedRecords) {
      try {
        const recordRef = doc(db, 'hostAvailability', orphan.recordId);
        await deleteDoc(recordRef);
        deleted.push({
          recordId: orphan.recordId,
          hostId: orphan.hostId,
          slotId: orphan.slotId
        });
      } catch (error) {
        console.error('Error deleting orphaned record:', orphan.recordId, error);
        failed.push({
          recordId: orphan.recordId,
          error: error.message
        });
      }
    }

    return {
      success: true,
      deleted: deleted.length,
      failed: failed.length,
      details: { deleted, failed }
    };
  } catch (error) {
    console.error('Error deleting orphaned availability:', error);
    return { success: false, error: error.message };
  }
};
